import { useCallback, useEffect, useRef, useState } from "react";
import { shopModeApi, type WaitingOrders } from "../api/shopMode";
import {
  freshOrderIds,
  freshnessFrom,
  sameWaitingOrders,
  type Freshness,
} from "../platform/waitingOrders";

/** How often the locked screen asks. Four calls a minute; the route caps 30. */
const POLL_MS = 15_000;

/**
 * Frozen so the shared empty state cannot be mutated into a lie. One stray
 * `orders.push()` anywhere would otherwise make every consumer's "nothing is
 * waiting" claim an order that does not exist.
 */
const EMPTY_WAITING: WaitingOrders = Object.freeze({
  orders: Object.freeze([]),
  totalEur: "0.00",
  totalIsPartial: false,
});

/** One stable empty array, so acknowledging cannot re-trigger its own effect. */
const NO_ARRIVALS: readonly string[] = Object.freeze([]);

export interface WaitingOrdersState {
  waiting: WaitingOrders;
  /**
   * Ids seen for the FIRST time since `acknowledge`. This is what an alert
   * fires on: announcing "there is an order" instead would re-announce the same
   * one four times a minute until somebody took it, which is how an alert
   * becomes wallpaper.
   */
  arrived: readonly string[];
  /** Clears `arrived`. The consumer MUST call it, or the list grows all day. */
  acknowledge: () => void;
  /**
   * How much `waiting` may be believed. `unknown` before the first successful
   * poll, `stale` once failures persist — a boolean conflated "I asked and the
   * answer was nothing" with "I have not managed to ask", which is how a tablet
   * armed during an outage showed a confident all-clear.
   */
  freshness: Freshness;
}

/**
 * G-79 — polls "is an order waiting at this shop?" on the LOCKED kiosk screen.
 *
 * Mounted only by the pre-PIN view: once a florist has signed in, the real
 * order panels are on screen and a summary banner would be a second, staler
 * answer to a question already better answered.
 *
 * The decisions live in `platform/waitingOrders.ts` as plain functions, because
 * they are the part with consequences and a hook cannot be exercised under this
 * repo's node test environment.
 */
export function useWaitingOrders(
  shopFloorSessionId: string | null,
): WaitingOrdersState {
  const [waiting, setWaiting] = useState<WaitingOrders>(EMPTY_WAITING);
  const [arrived, setArrived] = useState<readonly string[]>(NO_ARRIVALS);
  const [freshness, setFreshness] = useState<Freshness>("unknown");
  /** Every id already announced, so a second poll does not re-announce it. */
  const announcedRef = useRef<Set<string>>(new Set());
  const failuresRef = useRef(0);
  const everSucceededRef = useRef(false);

  // The SAME empty array every time, so clearing cannot change the identity
  // the chime effect depends on and re-enter it.
  const acknowledge = useCallback(() => setArrived(NO_ARRIVALS), []);

  useEffect(() => {
    if (!shopFloorSessionId) {
      setWaiting(EMPTY_WAITING);
      setArrived(NO_ARRIVALS);
      setFreshness("unknown");
      announcedRef.current = new Set();
      failuresRef.current = 0;
      everSucceededRef.current = false;
      return;
    }

    let cancelled = false;

    const poll = async () => {
      let next: WaitingOrders;
      try {
        next = await shopModeApi.waiting(shopFloorSessionId);
      } catch {
        if (cancelled) return;
        // A dropped poll is not evidence that nothing is waiting. Count it, and
        // let the screen say what it can and cannot see.
        failuresRef.current += 1;
        setFreshness(
          freshnessFrom(everSucceededRef.current, failuresRef.current),
        );
        return;
      }
      if (cancelled) return;

      failuresRef.current = 0;
      everSucceededRef.current = true;
      setFreshness("fresh");

      const fresh = freshOrderIds(announcedRef.current, next.orders);
      // Keep only what is LIVE, so an order that is released and re-offered
      // later is announced again rather than silently ignored forever.
      announcedRef.current = new Set(next.orders.map((order) => order.id));

      // Same answer as last time -> keep the old object so React bails out and
      // the locked screen does not re-render four times a minute all day.
      setWaiting((prev) => (sameWaitingOrders(prev, next) ? prev : next));
      if (fresh.length > 0) setArrived((prev) => [...prev, ...fresh]);
    };

    void poll();
    const timer = window.setInterval(() => void poll(), POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [shopFloorSessionId]);

  return { waiting, arrived, acknowledge, freshness };
}
